"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { useQueries, useQuery } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";

import {
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

import Pagination from "@/components/pagination";
import Button from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Skeleton } from "@/components/ui/skeleton";
import PlaceholderRow from "@/components/ui/table/placeholder-row";

import { cx } from "@/lib/tailwind-utils";

import type { NoteGroupItem } from "@/types/notes";
import type { Page } from "@/types/pagination";

import { Table, TableBody, TableCell, TableRow } from "./index";
import { CLINIC_GROUP_BY } from "@/api/analytics/clinic-report-scope";
import { useClinicReportScope } from "@/api/analytics/use-clinic-report-scope";

export type GroupByOption<T> = {
  id: string;
  label: string;
  getGroup: (row: T) => { key: string; label: string } | null | undefined;
  sort?: "firstRow" | "alpha";
};

export type Group<T> = {
  key: string;
  label: string;
  rows: T[];
  total?: number;
  /**
   * Position in a tree-shaped group set (0 = root). Defaults to 0 for flat groupings.
   * When set on a backend response (e.g. group_by="item_category"), the header is
   * indented by depth and tree-aware collapse hides every descendant when the
   * parent is collapsed.
   */
  depth?: number;
  /** Parent group's key in a tree-shaped grouping; null/undefined at the root. */
  parentKey?: string | null;
  /**
   * True when this row is a synthetic ancestor with no rows of its own. The
   * body renders only the header (descendants carry the data). A leaf-and-parent
   * row stays false so its rows still render.
   */
  isSynthetic?: boolean;
};

/** Group blocks the first-load skeleton stands in with, before the index says how many. */
const SKELETON_GROUPS = 3;
/** Capped well under a full page: several buckets hold only a row or two. */
const SKELETON_ROWS = 5;
/** Module-level so the skeleton's tables never re-render on a parent update. */
const EMPTY_SELECTION = new Set<string>();
const NOOP = () => {};

export function groupRows<T>(
  rows: T[],
  opt?: GroupByOption<T> | null,
): Group<T>[] {
  if (!opt) return [{ key: "__all__", label: "All", rows }];
  const map = new Map<string, Group<T>>();
  for (const row of rows) {
    const g = opt.getGroup(row) ?? { key: "none", label: "No category" };
    if (!map.has(g.key))
      map.set(g.key, { key: g.key, label: g.label, rows: [] });
    map.get(g.key)!.rows.push(row);
  }
  const groups = Array.from(map.values());
  if (opt.sort === "alpha")
    groups.sort((a, b) => a.label.localeCompare(b.label));

  return groups;
}

export type GroupedTableRef = {
  expandAll: () => void;
  collapseAll: () => void;
  isAllExpanded: boolean;
  isAllCollapsed: boolean;
  totalGroups: number;
};

type GroupedTableBodyProps<T> = {
  groups: Group<T>[];
  colSpan: number;
  collapsed?: Record<string, boolean>;
  onToggle?: (key: string) => void;
  onLoadMore?: (key: string) => void; // deprecated in favor of prev/next
  renderRow: (row: T, index: number) => ReactNode;
  renderRowInGroup?: (row: T, group: Group<T>) => ReactNode;
  rowsByGroup: Record<string, T[]>;
  pageByGroup?: Record<string, number>;
  perPage?: number;
  onPrevPage?: (key: string) => void;
  onNextPage?: (key: string) => void;
  loadingByGroup?: Record<string, boolean>;
  renderTableHead?: (group: Group<T>, idx: number) => ReactNode; // expects a <TableHead> element
  renderGroupFooter?: (group: Group<T>) => ReactNode;
  renderGroupLabel?: (group: Group<T>) => ReactNode; // custom group label renderer
  renderGroupActions?: (group: Group<T>) => ReactNode;
  label?: string;
  tableClassName?: string;
  size?: "md" | "sm";
  collapsible?: boolean;
  // Selection (scoped per group)
  selectable?: boolean;
  isGroupSelectable?: (group: Group<T>) => boolean;
  selectedRowsByGroup?: Record<string, Set<string>>;
  setSelectedRowsByGroup?: Dispatch<
    SetStateAction<Record<string, Set<string>>>
  >;
  // Groups to always show even when empty
  alwaysShowGroups?: Set<string>;
  // Ref to expose expand/collapse all
  tableRef?: RefObject<GroupedTableRef | null>;
  // Callback when expansion state changes (for syncing with ExpandCollapseGroupControls)
  onExpansionChange?: (state: GroupedTableExpansionState) => void;
  // Renders expand/collapse all buttons above the groups.
  // Defaults to true when no external tableRef is provided, false otherwise.
  showExpandCollapse?: boolean;
};

export function GroupedTableBody<T>({
  groups,
  colSpan,
  collapsed,
  onToggle,
  renderRow,
  renderRowInGroup,
  rowsByGroup,
  pageByGroup,
  perPage,
  onPrevPage,
  onNextPage,
  loadingByGroup,
  renderTableHead,
  renderGroupFooter,
  renderGroupLabel,
  renderGroupActions,
  label = "items",
  collapsible = true,
  tableClassName,
  size = "md",
  selectable,
  isGroupSelectable,
  selectedRowsByGroup,
  setSelectedRowsByGroup,
  alwaysShowGroups,
  tableRef,
  onExpansionChange,
  showExpandCollapse,
}: GroupedTableBodyProps<T>) {
  const shouldShowExpandCollapse =
    (showExpandCollapse ?? !tableRef) && collapsible;
  // Internal ref for expand/collapse controls when no external ref is provided
  const internalRef = useRef<GroupedTableRef | null>(null);
  const effectiveRef = tableRef ?? internalRef;

  const [internalCollapsed, setInternalCollapsed] = useState<
    Record<string, boolean>
  >(collapsed ?? {});
  useEffect(() => {
    if (collapsed) setInternalCollapsed(collapsed);
  }, [collapsed]);

  const toggle = (k: string) => {
    const next = { ...internalCollapsed, [k]: !internalCollapsed[k] };
    setInternalCollapsed(next);
    onToggle?.(k);
  };

  // For tree-shaped group sets, a group is rendered only when every ancestor
  // is expanded. Synthetic parents (depth set, no own rows but a rolled-up
  // total) still appear because their header is the affordance to expand them.
  const groupByKey = useMemo(() => {
    const map = new Map<string, Group<T>>();
    for (const g of groups) map.set(g.key, g);
    return map;
  }, [groups]);

  const isHiddenByCollapsedAncestor = useCallback(
    (g: Group<T>): boolean => {
      let current = g.parentKey ?? null;
      while (current) {
        if (internalCollapsed[current]) return true;
        current = groupByKey.get(current)?.parentKey ?? null;
      }
      return false;
    },
    [groupByKey, internalCollapsed],
  );

  const visibleGroupKeys = useMemo(
    () =>
      groups
        .filter((g) => {
          if (isHiddenByCollapsedAncestor(g)) return false;
          const rows = rowsByGroup[g.key] ?? [];
          const hasContent =
            rows.length > 0 ||
            loadingByGroup?.[g.key] ||
            alwaysShowGroups?.has(g.key) ||
            (g.total ?? 0) > 0;
          return hasContent;
        })
        .map((g) => g.key),
    [
      groups,
      rowsByGroup,
      loadingByGroup,
      alwaysShowGroups,
      isHiddenByCollapsedAncestor,
    ],
  );

  const collapsedCount = visibleGroupKeys.filter(
    (k) => internalCollapsed[k],
  ).length;
  const isAllExpanded = visibleGroupKeys.length > 0 && collapsedCount === 0;
  const isAllCollapsed =
    visibleGroupKeys.length > 0 && collapsedCount === visibleGroupKeys.length;

  const expandAll = useCallback(() => {
    setInternalCollapsed({});
    onExpansionChange?.({
      isAllExpanded: true,
      isAllCollapsed: false,
      totalGroups: visibleGroupKeys.length,
    });
  }, [visibleGroupKeys.length, onExpansionChange]);

  const collapseAll = useCallback(() => {
    setInternalCollapsed(
      Object.fromEntries(visibleGroupKeys.map((k) => [k, true])),
    );
    onExpansionChange?.({
      isAllExpanded: false,
      isAllCollapsed: true,
      totalGroups: visibleGroupKeys.length,
    });
  }, [visibleGroupKeys, onExpansionChange]);

  useImperativeHandle(
    effectiveRef,
    () => ({
      expandAll,
      collapseAll,
      isAllExpanded,
      isAllCollapsed,
      totalGroups: visibleGroupKeys.length,
    }),
    [
      expandAll,
      collapseAll,
      isAllExpanded,
      isAllCollapsed,
      visibleGroupKeys.length,
    ],
  );

  useEffect(() => {
    onExpansionChange?.({
      isAllExpanded,
      isAllCollapsed,
      totalGroups: visibleGroupKeys.length,
    });
  }, [
    isAllExpanded,
    isAllCollapsed,
    visibleGroupKeys.length,
    onExpansionChange,
  ]);

  // Tree-shaped groupings (group_by="item_category") let descendants render
  // directly under their parent header. The default `space-y-2` between groups
  // doubles up with the parent's `pb-1` and the Button's own padding, leaving
  // ~50px of dead space between a synthetic parent and its child. Collapse the
  // inter-group gap (and the header pb-1) so the tree reads as a single unit.
  const isTreeShape = groups.some((g) => (g.depth ?? 0) > 0);
  return (
    <div className={isTreeShape ? "space-y-0" : "space-y-2"}>
      {shouldShowExpandCollapse && visibleGroupKeys.length > 1 && (
        <div className="flex items-center gap-1">
          <ButtonGroup>
            <Button
              variant="secondary"
              size="sm"
              onClick={expandAll}
              disabled={isAllExpanded}
            >
              Expand All
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={collapseAll}
              disabled={isAllCollapsed}
            >
              Collapse All
            </Button>
          </ButtonGroup>
        </div>
      )}
      {groups.map((g, idx) => {
        const isCollapsed = internalCollapsed[g.key] ?? false;
        const rows = rowsByGroup[g.key] ?? [];
        const pages = pageByGroup?.[g.key] ?? 1;
        const total = g.total ?? rows.length;
        // When perPage is unset (unpaginated grouped view), fall back to the
        // group's rolled-up `total` so a tree-shaped parent with both own
        // rows and child descendants doesn't read a stale pageSize off
        // rows.length and render an enabled but dead Pagination control.
        const pageSize = (perPage ?? total) || 1;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        // Synthetic parents in a tree-shaped grouping (e.g. group_by="item_category"
        // rolled up to "Injectables") have a rolled-up total but no rows of their
        // own; their descendant groups carry every individual row. Render the
        // header so users can expand the subtree, and skip the empty rows table
        // underneath so the header stays uncluttered.
        //
        // We trust the server's explicit `isSynthetic` when present (e.g. from
        // weave_category_tree's is_synthetic); otherwise infer it from the
        // empty-rows-but-positive-total heuristic for older payloads. A bucket
        // waiting on its own page looks exactly like that too, so exclude it —
        // otherwise every page turn drops the group's table and its pagination.
        // Real synthetic ancestors never qualify: they skip their listQuery, so
        // `loadingByGroup` is pinned false for them.
        const isSyntheticParent =
          g.isSynthetic ??
          (!loadingByGroup?.[g.key] && rows.length === 0 && (g.total ?? 0) > 0);
        const isRowSelectable =
          !!selectable && (isGroupSelectable ? isGroupSelectable(g) : true);
        // Show group if it has rows, is loading, in alwaysShowGroups, or is a
        // synthetic-parent header in a tree-shaped grouping.
        const shouldShow =
          rows.length > 0 ||
          loadingByGroup?.[g.key] ||
          alwaysShowGroups?.has(g.key) ||
          isSyntheticParent;
        if (!shouldShow) return null;
        if (isHiddenByCollapsedAncestor(g)) return null;
        const depth = g.depth ?? 0;
        const parentLabel =
          depth > 0 && g.parentKey ? groupByKey.get(g.parentKey)?.label : null;
        return (
          <div key={g.key} style={{ paddingLeft: `${depth * 16}px` }}>
            <div
              className={cx(
                "sticky top-0 z-10 flex items-center justify-between",
                size === "sm"
                  ? "rounded-t-md bg-muted py-1 shadow-sm"
                  : isTreeShape
                    ? ""
                    : "pb-1",
              )}
            >
              {collapsible ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => toggle(g.key)}
                  aria-expanded={!isCollapsed}
                  icon={isCollapsed ? FaChevronRight : FaChevronDown}
                  className={cx(
                    "justify-start hover:!bg-transparent active:scale-100",
                    size === "sm" ? "h-6 gap-1" : "gap-2",
                  )}
                >
                  {renderGroupLabel ? (
                    renderGroupLabel(g)
                  ) : (
                    <>
                      <span className="flex items-center gap-1.5">
                        <span
                          className={cx(
                            "font-semibold",
                            size === "sm" ? "pl-2 text-body" : "text-body-lg",
                          )}
                        >
                          {g.label}
                        </span>
                        {parentLabel && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-body text-foreground-muted">
                            in {parentLabel}
                          </span>
                        )}
                      </span>
                      <span
                        className={cx(
                          "text-muted-foreground",
                          size === "sm" ? "text-caption" : "text-caption",
                        )}
                      >
                        <span>{g.total ?? rows.length}</span> {label}
                      </span>
                    </>
                  )}
                </Button>
              ) : renderGroupLabel ? (
                renderGroupLabel(g)
              ) : (
                <p
                  className={cx(
                    "font-semibold",
                    size === "sm" ? "pl-2 text-body" : "text-body-lg",
                  )}
                >
                  {g.label}
                </p>
              )}
              {renderGroupActions && (
                <div className="shrink-0">{renderGroupActions(g)}</div>
              )}
            </div>
            {!isCollapsed && !isSyntheticParent && (
              <>
                <Table
                  className={cx(
                    "relative w-full table-fixed overflow-visible",
                    size === "sm" ? "leading-snug" : "leading-normal",
                    tableClassName,
                  )}
                  selectable={isRowSelectable}
                  selectedRows={
                    (selectedRowsByGroup?.[g.key] as Set<string> | undefined) ??
                    new Set<string>()
                  }
                  setSelectedRows={(updater) =>
                    setSelectedRowsByGroup?.((prev) => {
                      const current = prev?.[g.key] ?? new Set<string>();
                      const next =
                        typeof updater === "function"
                          ? (updater as (p: Set<string>) => Set<string>)(
                              current,
                            )
                          : (updater as Set<string>);
                      return { ...(prev ?? {}), [g.key]: next };
                    })
                  }
                >
                  {renderTableHead ? renderTableHead(g, idx) : null}
                  <TableBody>
                    {loadingByGroup?.[g.key] ? (
                      // A selectable TableRow injects its own checkbox cell, so
                      // asking for colSpan here would make the skeleton one cell
                      // wider than the head and the rows it stands in for.
                      <PlaceholderRow
                        rows={perPage ?? 5}
                        headers={isRowSelectable ? colSpan - 1 : colSpan}
                      />
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          className="text-center text-body text-foreground-secondary"
                          colSpan={colSpan}
                        >
                          No {label.toLocaleLowerCase()} found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence initial={false}>
                        {rows.map((r, rIdx) =>
                          renderRowInGroup
                            ? renderRowInGroup(r, g)
                            : renderRow(r, rIdx + idx),
                        )}
                      </AnimatePresence>
                    )}
                    {renderGroupFooter ? renderGroupFooter(g) : null}
                  </TableBody>
                </Table>
                {totalPages > 1 && (
                  <Pagination
                    displayText={label}
                    total={total}
                    currentPage={pages ?? 1}
                    perPage={pageSize}
                    handlePageChange={(direction) => {
                      if (direction === "prev") {
                        onPrevPage?.(g.key);
                      } else {
                        onNextPage?.(g.key);
                      }
                    }}
                  />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// High-level orchestrator to encapsulate grouping queries and pagination
export type GroupByMeta<K extends string = string> = {
  id: string;
  label: string;
  paramKey: K;
};

type GroupedTableOrchestratorProps<Row, Filters, K extends string> = {
  filters: Filters;
  groupBy: GroupByMeta<K> | null;
  perPage?: number;
  headersCount: number;
  size?: "md" | "sm";
  // functions that return React Query options used by useQuery/useQueries
  groupsQuery: (
    paramKey: K,
    filters: Filters,
  ) => {
    queryKey: readonly unknown[];
    queryFn: () => Promise<NoteGroupItem[] | null>;
    select?: (data: NoteGroupItem[] | null) => NoteGroupItem[];
    enabled?: boolean;
  };
  listQuery: (filtersWithGroup: Filters) => {
    queryKey: readonly unknown[];
    queryFn: () => Promise<Page<Row> | null>;
    enabled?: boolean;
  };
  renderRow: (row: Row) => ReactNode;
  // Optional: override group label mapping if backend label is insufficient
  label?: string;
  labelForKey?: (key: string, backendLabel?: string) => string | undefined;
  // Optional comparator applied to groups before rendering. Useful when groups have
  // a natural ordering the backend doesn't know about — e.g., categories should sit
  // immediately after their parent so the tree reads top-to-bottom. Compares the raw
  // group index (NoteGroupItem) since sorting runs before the row-bearing Group<Row>
  // shape is built.
  compareGroups?: (a: NoteGroupItem, b: NoteGroupItem) => number;
  renderTableHead?: () => ReactNode;
  renderGroupFooter?: (group: Group<Row>) => ReactNode;
  renderGroupLabel?: (group: Group<Row>) => ReactNode;
  renderGroupActions?: (group: Group<Row>) => ReactNode;
  // Selection (scoped per group)
  selectable?: boolean;
  isGroupSelectable?: (group: Group<Row>) => boolean;
  selectedRowsByGroup?: Record<string, Set<string>>;
  setSelectedRowsByGroup?: Dispatch<
    SetStateAction<Record<string, Set<string>>>
  >;
  // Ref to expose expand/collapse all
  tableRef?: RefObject<GroupedTableRef | null>;
  // Callback when expansion state changes
  onExpansionChange?: (state: GroupedTableExpansionState) => void;
  // Optional controlled per-group page state, so a caller can keep the user's
  // place across navigation. Uncontrolled when omitted.
  groupPages?: Record<string, number>;
  onGroupPagesChange?: Dispatch<SetStateAction<Record<string, number>>>;
};

export function GroupedTableOrchestrator<
  Row,
  Filters extends Record<string, any>,
  K extends string = string,
>({
  filters,
  groupBy,
  perPage = 5,
  headersCount,
  size = "md",
  groupsQuery,
  listQuery,
  renderRow,
  label,
  labelForKey,
  compareGroups,
  renderTableHead,
  renderGroupFooter,
  renderGroupLabel,
  renderGroupActions,
  selectable,
  isGroupSelectable,
  selectedRowsByGroup,
  setSelectedRowsByGroup,
  tableRef,
  onExpansionChange,
  groupPages: controlledGroupPages,
  onGroupPagesChange,
}: GroupedTableOrchestratorProps<Row, Filters, K>) {
  const [internalGroupPages, setInternalGroupPages] = useState<
    Record<string, number>
  >({});
  const groupPages = controlledGroupPages ?? internalGroupPages;
  const setGroupPages = onGroupPagesChange ?? setInternalGroupPages;
  const pageFor = useCallback((k: string) => groupPages[k] || 1, [groupPages]);

  const groupsQueryOptions = groupsQuery(groupBy!.paramKey, filters);
  const { data: groups, isPending: isGroupsLoading } = useQuery({
    ...groupsQueryOptions,
    enabled: !!groupBy && groupsQueryOptions.enabled !== false,
  });

  const rawGroupIndex = groups ?? [];
  const groupIndex = useMemo(() => {
    if (!compareGroups) return rawGroupIndex;
    return [...rawGroupIndex].sort(compareGroups);
  }, [rawGroupIndex, compareGroups]);

  // Synthetic ancestors in a tree-shaped group set (e.g. "Injectables" with no
  // own stock but children Botox / Dysport) are headers only — descendants carry
  // the rows. Skip their per-group listQuery so we don't hit the backend with
  // `category=[parent]` only to get no rows back. A leaf-and-parent (own items
  // AND children) is NOT synthetic — its listQuery still fires.
  //
  // We trust the server's `isSynthetic` flag (set by weave_category_tree's
  // `is_synthetic` based on whether the row came from leaf_keys). Fall back to
  // the parent-key heuristic for older payloads that don't carry the field.
  const isSyntheticByServer = useMemo(() => {
    return groupIndex.some((g) => g.isSynthetic !== undefined);
  }, [groupIndex]);
  const isParentGroup = useMemo(() => {
    const set = new Set<string>();
    if (isSyntheticByServer) {
      for (const g of groupIndex) {
        if (g.isSynthetic) set.add(g.key);
      }
    } else {
      // Legacy: any group claimed as a parentKey by another group is treated as
      // synthetic. Wrong for leaf-and-parent, but kept as fallback for callers
      // that haven't been updated yet.
      for (const g of groupIndex) {
        if (g.parentKey != null) set.add(g.parentKey);
      }
    }
    return set;
  }, [groupIndex, isSyntheticByServer]);

  // Build per-group queries
  const groupQueries = useQueries({
    queries: groupIndex.map((g) => ({
      ...listQuery({
        ...filters,
        [groupBy!.paramKey]: g.key,
        page: pageFor(g.key),
        perPage,
      } as Filters),
      enabled: !!groupBy && !isParentGroup.has(g.key),
    })),
  });

  const rowsByGroup = useMemo(() => {
    const result: Record<string, Row[]> = {};
    groupIndex.forEach((g, idx) => {
      if (isParentGroup.has(g.key)) {
        result[g.key] = [];
        return;
      }
      const q = groupQueries[idx];
      const data = (q?.data as Page<Row> | undefined)?.data ?? [];
      result[g.key] = data as Row[];
    });
    return result;
  }, [groupIndex, groupQueries, isParentGroup]);

  const loadingByGroup = useMemo(() => {
    const result: Record<string, boolean> = {};
    groupIndex.forEach((g, idx) => {
      if (isParentGroup.has(g.key)) {
        result[g.key] = false;
        return;
      }
      const q = groupQueries[idx];
      result[g.key] = Boolean(q?.isPending);
    });
    return result;
  }, [groupIndex, groupQueries, isParentGroup]);

  // Clamp pages when group totals change
  useEffect(() => {
    // Wait for the real group index: while it loads it is empty, and clamping
    // against that would wipe a restored page back to 1.
    if (!groupBy || isGroupsLoading) return;
    setGroupPages((prev) => {
      const next: Record<string, number> = {};
      let changed = false;
      const keys = new Set<string>();
      groupIndex.forEach((g) => {
        keys.add(g.key);
        const totalPages = Math.max(1, Math.ceil((g.total || 0) / perPage));
        const current = prev[g.key] ?? 1;
        const clamped = Math.min(current, totalPages);
        next[g.key] = clamped;
        if (clamped !== current) changed = true;
      });
      for (const k of Object.keys(prev)) {
        if (!keys.has(k)) {
          changed = true;
          break;
        }
      }
      return changed ? next : prev;
    });
  }, [groupBy, groupIndex, isGroupsLoading, perPage, setGroupPages]);

  // If not grouped, render nothing (consumer handles flat mode)
  if (!groupBy) {
    return null;
  }

  if (isGroupsLoading) {
    // Which buckets exist is exactly what is still in flight, so stand in with
    // a fixed few. Same shape as the loaded state — group header, real columns,
    // placeholder rows — so the rows land in the grid this drew.
    return (
      <div className="space-y-2">
        {Array.from({ length: SKELETON_GROUPS }).map((_, index) => (
          <div key={index}>
            <div className="flex items-center gap-2 px-3 pb-1">
              <Skeleton className="size-3 rounded-sm" />
              {/* Group labels differ in length; equal bars read as a grid. */}
              <Skeleton
                className="h-4"
                style={{ width: `${9 - (index % 3) * 2}rem` }}
              />
              <Skeleton className="h-3 w-20" />
            </div>
            <Table
              aria-busy="true"
              className={cx(
                "relative w-full table-fixed overflow-visible",
                size === "sm" ? "leading-snug" : "leading-normal",
              )}
              selectable={selectable}
              selectedRows={EMPTY_SELECTION}
              setSelectedRows={NOOP}
            >
              {renderTableHead ? renderTableHead() : null}
              <TableBody>
                <PlaceholderRow
                  rows={Math.min(perPage, SKELETON_ROWS)}
                  headers={selectable ? headersCount - 1 : headersCount}
                />
              </TableBody>
            </Table>
          </div>
        ))}
      </div>
    );
  }

  const groupsForRender = groupIndex.map((g) => ({
    key: g.key,
    label: labelForKey?.(g.key, g.label) ?? g.label ?? g.key,
    total: g.total,
    rows: rowsByGroup[g.key] ?? [],
    depth: g.depth ?? 0,
    parentKey: g.parentKey ?? null,
    isSynthetic: g.isSynthetic,
  }));

  return (
    <GroupedTableBody<Row>
      groups={groupsForRender}
      colSpan={headersCount}
      rowsByGroup={rowsByGroup}
      renderRow={renderRow}
      pageByGroup={groupPages}
      perPage={perPage}
      loadingByGroup={loadingByGroup}
      renderTableHead={renderTableHead}
      renderGroupFooter={renderGroupFooter}
      renderGroupLabel={renderGroupLabel}
      renderGroupActions={renderGroupActions}
      size={size}
      onPrevPage={(key) =>
        setGroupPages((prev) => ({
          ...prev,
          [key]: Math.max(1, pageFor(key) - 1),
        }))
      }
      onNextPage={(key) =>
        setGroupPages((prev) => ({ ...prev, [key]: pageFor(key) + 1 }))
      }
      label={label}
      selectable={selectable}
      isGroupSelectable={isGroupSelectable}
      selectedRowsByGroup={selectedRowsByGroup}
      setSelectedRowsByGroup={setSelectedRowsByGroup}
      tableRef={tableRef}
      onExpansionChange={onExpansionChange}
    />
  );
}

// ============================================================================
// Expand/Collapse Controls for GroupedTableBody / GroupedTableOrchestrator
// ============================================================================

export type GroupedTableExpansionState = {
  isAllExpanded: boolean;
  isAllCollapsed: boolean;
  totalGroups: number;
};

type ExpandCollapseGroupControlsProps = {
  tableRef: RefObject<GroupedTableRef | null>;
  show?: boolean;
  /** Pass expansion state from onExpansionChange to avoid stale ref reads */
  expansionState?: GroupedTableExpansionState;
};

export function ExpandCollapseGroupControls({
  tableRef,
  show = true,
  expansionState,
}: ExpandCollapseGroupControlsProps) {
  const totalGroups =
    expansionState?.totalGroups ?? tableRef.current?.totalGroups ?? 0;
  const isAllExpanded =
    expansionState?.isAllExpanded ?? tableRef.current?.isAllExpanded ?? false;
  const isAllCollapsed =
    expansionState?.isAllCollapsed ?? tableRef.current?.isAllCollapsed ?? true;

  if (!show || !tableRef.current || totalGroups <= 1) {
    return null;
  }

  const { expandAll, collapseAll } = tableRef.current;

  return (
    <ButtonGroup>
      <Button
        variant="secondary"
        size="sm"
        onClick={expandAll}
        disabled={isAllExpanded}
      >
        Expand All
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={collapseAll}
        disabled={isAllCollapsed}
      >
        Collapse All
      </Button>
    </ButtonGroup>
  );
}

type GroupByDropdownProps = {
  options: { id: string; label: string }[];
  value: string | null;
  onChange: (id: string | null) => void;
};

export function GroupByDropdown({
  options,
  value,
  onChange,
}: GroupByDropdownProps) {
  // Use "none" as the internal value for "No Grouping" state
  const selectValue = value ?? "none";
  const clinicReport = useClinicReportScope();
  const shown = clinicReport
    ? [{ id: CLINIC_GROUP_BY, label: "Clinic" }, ...options]
    : options;

  const handleValueChange = (newValue: string) => {
    onChange(newValue === "none" ? null : newValue);
  };

  return (
    <Select value={selectValue} onValueChange={handleValueChange}>
      <SelectTrigger className="max-h-7 w-auto border py-1 pl-4 pr-2 text-body-strong shadow-sm">
        <SelectValue placeholder="No Grouping" />
      </SelectTrigger>
      <SelectContent>
        {shown.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            Group By {o.label}
          </SelectItem>
        ))}
        <SelectItem value="none">No Grouping</SelectItem>
      </SelectContent>
    </Select>
  );
}
